<!--
  Confirm a detach over the list the row is in, instead of routing away to
  `…/:childModelKey/detach` and back.

  It posts the SAME request the detach PAGE does — hard-delete the join record
  by its own id, `byForce: true` — so what reaches the API is unchanged. Only
  where the confirmation happens moves.

  Opened through the store: the row's Detach button is mounted imperatively by
  a column formatter and has no parent to $emit to (see
  store/modules/modelDetachDialog.js). A list mounting this registers itself as
  a host, which is what tells that button a dialog exists here at all; where
  none does, it routes as it always has.

  The sentence is composed from the row rather than from a second read. A join
  row carries both sides of the relation already (`{ InternalRole, InternalUser }`),
  and which of them is the SUBJECT depends on whose page you are on: the far
  side is what you are detaching, the near side is what you are detaching it
  from. The page it replaces asked the API for those names and asked with the
  wrong id, so it rendered the ids — or nothing — instead.
-->
<template>
  <v-dialog
    v-model="isOpen"
    max-width="480"
    :persistent="api.isLoading"
    content-class="cdd-dialog"
    @click:outside="close"
  >
    <div v-if="isOpen" class="cdd-modal">
      <div class="cdd-modal__head">
        <span class="cdd-modal__icon" aria-hidden="true">
          <v-icon size="18">mdi-link-variant-off</v-icon>
        </span>
        <h2 class="cdd-modal__title">Detach {{ subjectLabel }}</h2>
        <button class="cdd-modal__close" title="Close" @click="close">
          <v-icon size="18">mdi-close</v-icon>
        </button>
      </div>

      <div class="cdd-modal__body">
        <AError :api="api" />

        <p class="cdd-modal__text">
          <template v-if="subjectName && contextName">
            Detach {{ subjectLabel.toLowerCase() }}
            <b>{{ subjectName }}</b> from {{ contextLabel.toLowerCase() }}
            <b>{{ contextName }}</b
            >?
          </template>
          <template v-else>
            Detach this {{ subjectLabel.toLowerCase() }}?
          </template>
        </p>
        <p class="cdd-modal__note">
          The two records stay; only the link between them is removed.
        </p>
      </div>

      <div class="cdd-modal__foot">
        <button
          class="cdd-modal__btn cdd-modal__btn--ghost"
          type="button"
          @click="close"
        >
          Cancel
        </button>
        <v-btn
          class="cdd-modal__btn cdd-modal__btn--danger"
          depressed
          :loading="api.isLoading"
          @click="submit"
        >
          Detach
        </v-btn>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import Api from "@/objects/api";
import Model from "@/objects/model";
import { mapState } from "vuex";

export default {
  name: "BreadModelChildDetachDialog",
  props: {
    /**
     * The key of the model whose page this list is on. It decides which side of
     * the join is the subject: on a user's Roles tab you detach the ROLE.
     */
    contextModelKey: {
      type: String,
      default: null,
    },
  },
  computed: {
    ...mapState({
      detachRequest: (state) => state.modelDetachDialog.data.request,
    }),
    /**
     * The join model — the record actually being deleted. Derived from the
     * request rather than resolved into `data`, because Model.getByKey() COPIES
     * the definition onto a bare instance and Vue 2 cannot see properties added
     * to an observed object after the fact: everything below reads `name` and
     * `parents`, and off a data-held instance none of it would ever invalidate.
     */
    joinModel() {
      const model = new Model();
      if (this.request) model.getByKey(this.request.childModelKey);
      return model;
    },
    /** The far side of the join — what is being detached. */
    subjectModelKey() {
      const parents = this.joinModel.parents || [];
      const other = parents.filter((key) => key !== this.contextModelKey);
      // With no context to go on, or a join that doesn't name this page as one
      // of its parents, there is no "far side" to single out.
      return other.length === 1 ? other[0] : null;
    },
    /**
     * Intermediate models name themselves per side — internalRoleToInternalUser
     * is "Role" seen from a user — which is what `name[contextModelKey]` holds.
     */
    subjectLabel() {
      const names = this.joinModel.name || {};
      const scoped = names[this.contextModelKey] || names;
      return scoped.singular || "record";
    },
    contextLabel() {
      return this.modelLabel(this.contextModelKey);
    },
    subjectName() {
      return this.recordName(this.subjectModelKey);
    },
    contextName() {
      return this.recordName(this.contextModelKey);
    },
  },
  data: () => ({
    api: new Api(),
    request: null,
    isOpen: false,
  }),
  watch: {
    /**
     * The store request is a one-shot: it is cleared as soon as it is taken, so
     * detaching from the same row twice still arrives as a change.
     */
    detachRequest(request) {
      if (!request) return;
      this.$store.dispatch("clearModelDetachRequest");
      this.open(request);
    },
  },
  created() {
    this.$store.dispatch("registerModelDetachDialogHost");
  },
  beforeDestroy() {
    this.$store.dispatch("unregisterModelDetachDialogHost");
  },
  methods: {
    open(request) {
      // Assigned first: `joinModel` is computed off it, and the URL below is
      // built from that.
      this.request = request;

      // Not callbackReset(): despite the name that is Api's pre-flight hook and
      // sets isLoading TRUE, which through `:persistent` would open the dialog
      // spinning and refusing to close.
      this.api.isError = false;
      this.api.error = null;

      this.api.setMethod("POST");
      this.api.setUrl(this.deleteUrl(request.intermediateModelId));
      this.api.setParams({ byForce: true });
      this.api.setCallbackCompleted(() => {
        this.isOpen = false;
        this.$emit("detached", { childModelKey: this.joinModel.key });
        this.$store.dispatch("showMessage", {
          message: "Detached successfully.",
          messageType: "success",
          timeout: 2000,
        });
      });

      this.isOpen = true;
    },
    /**
     * The join record is hard-deleted by its own id — the request the detach
     * page sends. Built the way PageServiceModelDeleteHard builds the same
     * call, i.e. off the model being deleted: the page keyed the service prefix
     * on the INTERMEDIATE model instead, which is the same service for
     * internalRoleToInternalUser but sends crmServicePermissionToInternalRole —
     * a crm model — at sso.
     */
    deleteUrl(id) {
      const base = this.$service[this.joinModel.serviceKey];
      const apiUrls = this.joinModel.apiUrls || {};
      if (apiUrls.apiHardDeleteUrl) {
        return `${base}${apiUrls.apiHardDeleteUrl}/${id}/delete/hard`;
      }
      return `${base}/v1/en/console/tabulator/model/${this.joinModel.key}/${id}/delete/hard`;
    },
    close() {
      if (this.api.isLoading) return;
      this.isOpen = false;
    },
    submit() {
      this.api.fetch();
    },
    modelLabel(modelKey) {
      if (!modelKey) return "record";
      const model = new Model();
      model.getByKey(modelKey);
      return (model.name && model.name.singular) || "record";
    },
    /**
     * The related record's own display column, off the row the button was drawn
     * on — `row.InternalRole.name` for the role side of a user's Roles tab.
     */
    recordName(modelKey) {
      if (!modelKey || !this.request || !this.request.row) return null;
      const record = this.request.row[this.$_ucfirst(modelKey)];
      if (!record || typeof record !== "object") return null;
      const model = new Model();
      model.getByKey(modelKey);
      const textColumn = model.column && model.column.text;
      return (textColumn ? record[textColumn] : record.name) || null;
    },
  },
};
</script>

<style scoped>
/* Same three tiers as the other dev-model dialogs: chrome on --app-card, the
   body a step back on --app-surface. Token-only colour, so dark needs no
   companion block. */
.cdd-modal {
  background: var(--app-card);
  font-family: var(--tbl-font);
}

.cdd-modal__head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 20px;
  background: var(--app-card);
  border-bottom: 1px solid var(--app-border);
}

/* The action is destructive, so it wears the danger colour where the eye lands
   first rather than only on the button that commits it. */
.cdd-modal__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 9px;
  background: var(--app-danger-bg);
  border: 1px solid var(--app-danger-border);
  color: var(--app-danger-fg);
}

.cdd-modal__icon .v-icon {
  color: var(--app-danger-fg);
}

.cdd-modal__title {
  flex: 1;
  min-width: 0;
  font-size: 0.98rem;
  font-weight: 700;
  color: var(--app-text);
  margin: 0;
}

.cdd-modal__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--app-text-faint);
  transition: all 0.15s ease;
}

.cdd-modal__close:hover {
  background: var(--app-hover);
  color: var(--app-text);
}

.cdd-modal__body {
  padding: 20px;
  background: var(--app-surface);
}

.cdd-modal__text {
  font-size: 0.88rem;
  font-weight: 600;
  line-height: 1.5;
  color: var(--app-text);
  margin: 0;
}

.cdd-modal__text b {
  font-weight: 800;
}

.cdd-modal__note {
  font-size: 0.76rem;
  font-weight: 600;
  color: var(--app-text-faint);
  margin: 8px 0 0;
}

.cdd-modal__foot {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px;
  border-top: 1px solid var(--app-border);
  background: var(--app-card);
}

.cdd-modal__btn {
  height: 38px;
  padding: 0 20px;
  border-radius: 10px;
  font-family: var(--tbl-font);
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: normal;
  text-transform: none;
  transition: all 0.15s ease;
}

.cdd-modal__btn--ghost {
  border: 1px solid var(--app-border-strong);
  background: transparent;
  color: var(--app-text-muted);
}

.cdd-modal__btn--ghost:hover {
  background: var(--app-hover);
  color: var(--app-text);
}

.cdd-modal__btn--danger.v-btn {
  background: var(--app-danger) !important;
  color: #fff !important;
  box-shadow: none;
}

.cdd-modal__btn--danger.v-btn:hover {
  filter: brightness(0.94);
}

@media (max-width: 599px) {
  .cdd-modal__head,
  .cdd-modal__foot {
    padding: 12px;
  }

  .cdd-modal__body {
    padding: 14px 12px;
  }

  .cdd-modal__btn {
    flex: 1;
  }
}
</style>

<style>
/* Unscoped: VDialog moves this content out of the component, and `content-class`
   lands on an element it owns rather than on our root, so a scoped selector
   never reaches either. */
.v-dialog.cdd-dialog {
  border-radius: 14px;
  background: var(--app-card);
  border: 1px solid var(--app-border);
  box-shadow: 0 24px 60px var(--app-shadow-deep, rgba(0, 0, 0, 0.35));
}
</style>
